import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import Input from '../../components/Input/Input';
import { useDispatch, useSelector } from 'react-redux';
import { createUser } from '../../redux/actions/user/createUser';
import Loading from '../../components/Loading/Loading';
import Swal from 'sweetalert2';
import { resetCreateUser, resetUpdateUserMaxEmployers, resetUpdateUserPermissions } from '../../redux/slices/user.slice';
import { createEmployer } from '../../redux/actions/employer/createEmployer';
import { resetCreateEmployer } from '../../redux/slices/employer.slice';
import PermissionsCheckboxList from '../../components/Permissions/PermissionsCheckboxList';
import { updateUserPermissions } from '../../redux/actions/user/updateUserPermissions';
import { usePermissions } from '../../hooks/usePermissions';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import { PERMISSIONS, resolvePermissionForApi } from '../../constants/permissions';
import { toast } from 'react-toastify';
import { updateUserMaxEmployers } from '../../redux/actions/user/updateUserMaxEmployers';
import { getMe } from '../../redux/actions/user/getMe';

const EMPLOYERS_LIMIT_PERMISSION = 'change_user_employers';
const MAX_EMPLOYERS_LIMIT = 2147483647;
const BROADCAST_MODERATION_PERMISSION = 'change_vacancy_broadcast_for_moderation_action';
const ACCEPT_MODERATION_PERMISSION = 'accept_moderation';
const createdStaffCredentialsStore = {
  items: [],
  isClosed: false,
};

const toIntegerOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) {
    return null;
  }

  return numericValue;
};

const formatQuotaValue = (value) => (Number.isInteger(value) ? value : '—');

function AddUser() {
  const { role } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission, isSuperuser, isLoaded } = usePermissions();
  const [createdUserId, setCreatedUserId] = useState(null);
  const [pendingQuotaPayload, setPendingQuotaPayload] = useState(null);
  const [createdStaffCredentials, setCreatedStaffCredentials] = useState(createdStaffCredentialsStore.items);
  const [isCreatedStaffAlertClosed, setIsCreatedStaffAlertClosed] = useState(createdStaffCredentialsStore.isClosed);

  const {
    getMe: { data: getMeData },
    createUser: { data: createUserData, error: createUserError, loading: createUserLoading },
    updateUserPermissions: { data: updatePermData, error: updatePermError, loading: updatePermLoading },
    updateUserMaxEmployers: { data: updateMaxEmployersData, error: updateMaxEmployersError, loading: updateMaxEmployersLoading },
  } = useSelector((state) => state.user);
  const {
    createEmployer: { data: createEmployerData, error: createEmployerError, loading: createEmployerLoading },
  } = useSelector((state) => state.employer);

  const form = useForm({
    defaultValues: {
      email: '',
      fio: '',
      type: 'ie',
      permissions: {},
      quota: '',
    },
  });

  useEffect(() => {
    dispatch(resetCreateUser());
    dispatch(resetUpdateUserPermissions());
    dispatch(resetUpdateUserMaxEmployers());
  }, [dispatch]);

  useEffect(() => {
    if (role !== 'admin' && role !== 'manager' && role !== 'user') {
      navigate('/');
    }
  }, [role, navigate]);

  const onSubmit = (data) => {
    if (role !== 'user' && isEmployersLimitEnabled && isQuotaBlockedByMissingData) {
      toast.error('Не удалось загрузить исходный лимит. Сохранение лимита временно недоступно.', {
        theme: 'colored',
        autoClose: '3000',
      });
      return;
    }

    if (role === 'user') {
      dispatch(
        createEmployer({
          name: data.fio,
          email: data.email,
          type: data.type,
        }),
      );
    } else {
      dispatch(
        createUser({
          full_name: data.fio,
          email: data.email,
          ...(role === 'admin' ? { type: 'staff', role: 'admin' } : { type: 'staff', role: 'manager' }),
        }),
      );
    }
  };

  const getErrorMessage = (error, fallbackText) => {
    if (!error) {
      return fallbackText;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (typeof error?.detail === 'string') {
      return error.detail;
    }

    if (Array.isArray(error?.detail) && error.detail.length) {
      return error.detail.join(', ');
    }

    if (typeof error?.message === 'string') {
      return error.message;
    }

    const firstFieldError = Object.values(error).find((value) => (Array.isArray(value) && value.length > 0) || typeof value === 'string');

    if (Array.isArray(firstFieldError)) {
      return firstFieldError[0];
    }

    if (typeof firstFieldError === 'string') {
      return firstFieldError;
    }

    return fallbackText;
  };

  useEffect(() => {
    if ((role === 'admin' || role === 'manager') && createUserError) {
      toast.error(getErrorMessage(createUserError, 'Не удалось создать пользователя. Попробуйте снова.'), {
        theme: 'colored',
        autoClose: '3000',
      });
      setCreatedUserId(null);
      setPendingQuotaPayload(null);
      dispatch(resetCreateUser());
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
    }
  }, [createUserError, role, dispatch]);

  // После создания пользователя - обновляем полномочия
  useEffect(() => {
    if (createUserData && !createdUserId) {
      const newUserId = createUserData.data.id;
      setCreatedUserId(newUserId);

      // Формируем массив полномочий для API
      const permissionsData = form.getValues('permissions');
      const permissionNames = new Set(
        Object.entries(permissionsData)
          .filter(([_, checked]) => checked)
          .map(([name]) => resolvePermissionForApi(name)),
      );

      if (permissionsData[BROADCAST_MODERATION_PERMISSION]) {
        permissionNames.add(ACCEPT_MODERATION_PERMISSION);
      }

      const requestedQuota = toIntegerOrNull(form.getValues('quota')) ?? 0;
      const shouldUpdateQuota = permissionNames.has(EMPLOYERS_LIMIT_PERMISSION) && requestedQuota > 0;
      setPendingQuotaPayload({
        userId: newUserId,
        max_employers: requestedQuota,
        shouldUpdateQuota,
      });

      if (permissionNames.size > 0) {
        permissionNames.add('view_user');
        if (permissionNames.has('add_manager')) {
          permissionNames.add('add_user');
          permissionNames.add('change_user_permissions');
          permissionNames.add('view_permissions');
        }

        const permissionsArray = Array.from(permissionNames).map((name) => ({ name }));
        dispatch(
          updateUserPermissions({
            userId: newUserId,
            permissions: permissionsArray,
          }),
        );
      } else if (shouldUpdateQuota) {
        dispatch(updateUserMaxEmployers({ userId: newUserId, max_employers: requestedQuota }));
      } else {
        // Если полномочий нет - сразу показываем успех
        showSuccessAndReset();
      }
    }
  }, [createUserData]);

  // После обновления полномочий - показываем успех
  useEffect(() => {
    if (updatePermData && createdUserId) {
      if (pendingQuotaPayload?.shouldUpdateQuota && pendingQuotaPayload?.userId === createdUserId) {
        dispatch(updateUserMaxEmployers({ userId: createdUserId, max_employers: pendingQuotaPayload.max_employers }));
        return;
      }
      showSuccessAndReset();
    }
  }, [updatePermData, createdUserId, pendingQuotaPayload, dispatch]);

  useEffect(() => {
    if (updateMaxEmployersData && createdUserId) {
      showSuccessAndReset();
    }
  }, [updateMaxEmployersData, createdUserId]);

  useEffect(() => {
    if ((role === 'admin' || role === 'manager') && updatePermError) {
      toast.error(getErrorMessage(updatePermError, 'Пользователь создан, но не удалось сохранить полномочия.'), {
        theme: 'colored',
        autoClose: '3000',
      });
      setCreatedUserId(null);
      setPendingQuotaPayload(null);
      dispatch(resetCreateUser());
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
    }
  }, [updatePermError, role, dispatch]);

  useEffect(() => {
    if ((role === 'admin' || role === 'manager') && updateMaxEmployersError) {
      toast.error(getErrorMessage(updateMaxEmployersError, 'Пользователь и полномочия сохранены, но не удалось сохранить лимит контрагентов.'), {
        theme: 'colored',
        autoClose: '3000',
      });
      setCreatedUserId(null);
      setPendingQuotaPayload(null);
      dispatch(resetCreateUser());
      dispatch(resetUpdateUserPermissions());
      dispatch(resetUpdateUserMaxEmployers());
    }
  }, [updateMaxEmployersError, role, dispatch]);

  const showSuccessAndReset = () => {
    if ((role === 'admin' || role === 'manager') && createUserData?.data?.email) {
      const nextCredentials = [
        ...createdStaffCredentialsStore.items,
        {
          id: createUserData.data.id ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          email: createUserData.data.email,
          pass: createUserData.pass ?? '',
        },
      ];

      createdStaffCredentialsStore.items = nextCredentials;
      createdStaffCredentialsStore.isClosed = false;
      setCreatedStaffCredentials(nextCredentials);
      setIsCreatedStaffAlertClosed(false);
    }

    dispatch(getMe());
    Swal.fire({
      title: 'Пользователь создан. Пароль отправлен на почту пользователя',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
    });
    form.reset();
    setCreatedUserId(null);
    setPendingQuotaPayload(null);
    dispatch(resetCreateUser());
    dispatch(resetUpdateUserPermissions());
    dispatch(resetUpdateUserMaxEmployers());
  };

  useEffect(() => {
    if (createEmployerData) {
      Swal.fire({
        title: 'Контрагент создан. Пароль отправлен на почту пользователя',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });
      form.reset();
      dispatch(resetCreateEmployer());
    }
  }, [createEmployerData]);

  const types = [
    { label: 'ИП', value: 'ie' },
    { label: 'Организация', value: 'company' },
    { label: 'Физическое лицо', value: 'individual' },
  ];

  const typeWatch = form.watch('type');
  const quotaRawValue = form.watch('quota');
  const selectedPermissions = form.watch('permissions') || {};
  const quotaValue = toIntegerOrNull(quotaRawValue);
  const quotaNumericValue = quotaRawValue === '' || quotaRawValue === null || quotaRawValue === undefined ? null : Number(quotaRawValue);
  const isEmployersLimitEnabled = Boolean(selectedPermissions[EMPLOYERS_LIMIT_PERMISSION]);
  const ownOriginalQuota = toIntegerOrNull(getMeData?.original_quota);
  const ownAvailableQuota = toIntegerOrNull(getMeData?.max_employers);
  const quotaPreviewValue = Number.isFinite(quotaNumericValue) ? quotaNumericValue : (quotaValue ?? 0);
  const ownAvailableAfterTransfer = Number.isInteger(ownAvailableQuota) ? ownAvailableQuota - quotaPreviewValue : null;
  const editableQuotaLimit = isSuperuser ? MAX_EMPLOYERS_LIMIT : (ownAvailableQuota ?? 0);
  const isQuotaExceeded = !isSuperuser && Number.isFinite(quotaNumericValue) && quotaNumericValue > editableQuotaLimit;
  const isQuotaBlockedByMissingData = !isSuperuser && (!Number.isInteger(ownOriginalQuota) || !Number.isInteger(ownAvailableQuota));
  const isLoading = createUserLoading || createEmployerLoading || updatePermLoading || updateMaxEmployersLoading;
  const showPermissions = role === 'admin' || role === 'manager';
  const quotaRules = {
    required: { value: isEmployersLimitEnabled, message: '*Обязательное поле для заполнения' },
    validate: (value) => {
      if (!isEmployersLimitEnabled) {
        return true;
      }

      if (isQuotaBlockedByMissingData) {
        return 'Не удалось загрузить исходный лимит для редактирования';
      }

      if (value === '' || value === null || value === undefined) {
        return 'Введите лимит контрагентов';
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue) || !Number.isInteger(numericValue)) {
        return 'Лимит должен быть целым числом';
      }

      if (numericValue < 0) {
        return 'Лимит не может быть отрицательным';
      }

      if (numericValue > MAX_EMPLOYERS_LIMIT) {
        return `Максимальное значение: ${MAX_EMPLOYERS_LIMIT}`;
      }

      if (!isSuperuser && numericValue > editableQuotaLimit) {
        return `Доступно для выдачи не более ${editableQuotaLimit}`;
      }

      return true;
    },
  };
  const quotaRegister = form.register('quota', quotaRules);

  const handleQuotaBlur = () => {
    if (isSuperuser) {
      return;
    }

    const currentValue = Number(form.getValues('quota'));
    if (!Number.isFinite(currentValue)) {
      return;
    }

    if (currentValue > editableQuotaLimit) {
      form.setValue('quota', editableQuotaLimit, { shouldDirty: true, shouldValidate: true });
    }
  };

  // Проверка доступа для создания админа - только superuser
  if (isLoaded && role === 'admin' && !isSuperuser) {
    return <AccessDenied />;
  }
  // Проверка доступа для создания менеджера
  if (isLoaded && role === 'manager' && !hasPermission(PERMISSIONS.ADD_MANAGER)) {
    return <AccessDenied />;
  }

  const closeCreatedStaffAlert = () => {
    createdStaffCredentialsStore.isClosed = true;
    setIsCreatedStaffAlertClosed(true);
  };

  return (
    <div className="w-full flex flex-1 mt-10 p-1">
      <div className="container relative flex flex-1 flex-col mx-auto max-w-[1163px]">
        <div className="flex flex-col sm:flex-row w-full gap-3">
          <div className="flex w-full sm:w-[20%] mb-2 justify-center">
            <div className="bg-[#E5E5E5] w-[160px] h-[160px] m-5 flex items-center justify-center rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="64px" height="64px" viewBox="0 0 24 24">
                <path fill="rgba(0,0,0,0.6)" d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4" />
              </svg>
            </div>
          </div>
          <div className="flex flex-col w-full sm:w-[50%] mb-2 p-5 gap-3">
            <h2 className="text-[25px] font-normal text-black">Добавить {role === 'user' ? 'контрагента' : role === 'admin' ? 'админа' : role === 'manager' ? 'менеджера' : ''}</h2>

            {role === 'user' && (
              <fieldset className="flex w-full first:flex-row xs:flex-col gap-4 mb-1">
                <legend className="mb-4 text-[14px]">Тип пользователя</legend>
                <Controller
                  control={form.control}
                  name="type"
                  render={({ field: { onChange, ...props } }) =>
                    types.map((option) => (
                      <div className="relative flex gap-x-3" key={option.value}>
                        <div className="flex h-6 items-center">
                          <input type="radio" id={option.value} {...props} onChange={onChange} value={option.value} checked={typeWatch === option.value} className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500" />
                        </div>
                        <div className="text-sm leading-6">
                          <label htmlFor={option.value} className="font-medium text-gray-900">
                            {option.label}
                          </label>
                        </div>
                      </div>
                    ))
                  }
                />
              </fieldset>
            )}

            <div className="">
              <Input form={form} name="fio" placeholder="ФИО" disabled={isLoading} />
            </div>
            <Input
              form={form}
              name="email"
              placeholder="Email"
              rules={{
                required: { value: true, message: '*Обязательное поле для заполнения' },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Неверный формат почты',
                },
              }}
              disabled={isLoading}
            />

            {/* Чекбоксы полномочий */}
            {showPermissions && (
              <div className="mt-4 p-4 border rounded-lg bg-gray-50 select-none">
                <PermissionsCheckboxList form={form} name="permissions" disabled={isLoading} />

                {isEmployersLimitEnabled && (
                  <div className="mt-0 pt-0 rounded-md border border-gray-200 bg-white p-4 space-y-3">
                    <div>
                      {!isSuperuser && <p className={`text-xs mt-1 ${isQuotaExceeded ? 'text-red-500' : 'text-gray-500'}`}>Доступно для выдачи: {formatQuotaValue(ownAvailableQuota)}</p>}
                      {isQuotaBlockedByMissingData && <p className="text-xs text-red-500 mt-1">Не удалось загрузить исходный лимит. Сохранение лимита заблокировано.</p>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div className="rounded-md border border-gray-200 p-3">
                        <p className="font-medium text-gray-800">Ваш лимит</p>
                        <p className="text-gray-600 mt-1">Всего: {isSuperuser ? '∞' : formatQuotaValue(ownOriginalQuota)}</p>
                        <p className={isQuotaExceeded ? 'text-red-500' : 'text-gray-600'}>Доступно: {isSuperuser ? '∞' : formatQuotaValue(ownAvailableAfterTransfer)}</p>
                      </div>
                      <div className="rounded-md border border-gray-200 p-3">
                        <p className="font-medium text-gray-800">Лимит нового пользователя</p>
                        <p className="text-gray-600 mt-1">Всего: {quotaPreviewValue}</p>
                        <p className={isQuotaExceeded ? 'text-red-500' : 'text-gray-600'}>Доступно: {quotaPreviewValue}</p>
                      </div>
                    </div>

                    <input
                      type="number"
                      placeholder="Введите лимит контрагентов"
                      disabled={isLoading || isQuotaBlockedByMissingData}
                      className={`w-full block rounded-md border border-1 border-gray-300 focus:border-button px-3.5 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none sm:text-sm sm:leading-6 ${form.formState.errors?.quota?.message ? 'border-red-300 focus:border-red-500 placeholder:text-red-400' : ''}`}
                      {...quotaRegister}
                      onBlur={(e) => {
                        quotaRegister.onBlur(e);
                        handleQuotaBlur();
                      }}
                    />
                    {form.formState.errors?.quota?.message && <div className="text-sm text-red-400 mt-1">{form.formState.errors.quota.message}</div>}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-row gap-3 relative">
              <button onClick={form.handleSubmit(onSubmit)} type="button" className="text-center border rounded py-2 px-4 bg-button text-white hover:bg-button-hov mr-2 w-full md:w-auto transition ease-in-out duration-300" disabled={isLoading}>
                Сохранить
              </button>
              <button onClick={() => navigate(-1)} type="button" className="text-center border rounded py-2 px-4 text-button border-button border-1 w-full md:w-auto hover:text-white bg-white hover:bg-button transition ease-in-out duration-300">
                Отмена
              </button>
              {isLoading && (
                <div className="translate-x-5">
                  <Loading />
                </div>
              )}
            </div>
          </div>
        </div>

        {(role === 'admin' || role === 'manager') && createdStaffCredentials.length > 0 && !isCreatedStaffAlertClosed && (
          <div className="relative mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <button type="button" onClick={closeCreatedStaffAlert} className="absolute right-3 top-3 text-amber-700 transition hover:text-amber-900" aria-label="Закрыть алерт">
              ×
            </button>
            <p className="text-sm font-semibold text-amber-900">Созданные пользователи</p>
            <div className="mt-3 space-y-2">
              {createdStaffCredentials.map((user) => (
                <div key={user.id} className="rounded-md border border-amber-200 bg-white p-3 text-sm text-amber-900">
                  <p>
                    Почта: <span className="font-medium">{user.email}</span>
                  </p>
                  <p>
                    Пароль: <span className="font-medium">{user.pass}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AddUser;
